import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api/client";
import { downloadInvoice } from "../utils/invoice";

const STATUS_BADGES = {
  created: { bg: "#fff7ed", fg: "#9a3412", label: "Awaiting Payment" },
  paid: { bg: "#dcfce7", fg: "#166534", label: "Payment Confirmed" },
  accepted: { bg: "#dbeafe", fg: "#1d4ed8", label: "Accepted" },
  "pending-shipping": { bg: "#fef3c7", fg: "#92400e", label: "Packed" },
  shipped: { bg: "#e0e7ff", fg: "#3730a3", label: "Shipped" },
  delivered: { bg: "#def7ec", fg: "#03543f", label: "Delivered" },
  cancelled: { bg: "#fee2e2", fg: "#991b1b", label: "Cancelled" },
  refunded: { bg: "#fef3c7", fg: "#92400e", label: "Refunded" },
  failed: { bg: "#fee2e2", fg: "#991b1b", label: "Payment Failed" },
};

const STEP_META = [
  { status: "created", label: "Order placed" },
  { status: "paid", label: "Payment confirmed" },
  { status: "accepted", label: "Accepted" },
  { status: "pending-shipping", label: "Packed" },
  { status: "shipped", label: "Shipped" },
  { status: "delivered", label: "Delivered" },
];

const FINAL_STEP_META = {
  cancelled: { label: "Cancelled" },
  refunded: { label: "Refunded" },
  failed: { label: "Payment failed" },
};

const formatCurrency = (value) => `₹${Number(value || 0).toFixed(2)}`;

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const formatDateOnly = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
};

const getOrderRows = (order) =>
  Array.isArray(order.items) && order.items.length
    ? order.items
    : order.item
      ? [{ item: order.item, qty: 1, price: order.item.price }]
      : [];

const getStatusEntryMap = (order) => {
  const entries = Array.isArray(order.statusHistory) ? order.statusHistory : [];
  return new Map(entries.map((entry) => [entry.status, entry]));
};

const buildTimeline = (order) => {
  const currentStatus = order.status || "created";
  const historyMap = getStatusEntryMap(order);
  const currentIndex = STEP_META.findIndex((step) => step.status === currentStatus);

  const standardSteps = STEP_META.map((step, index) => {
    const history = historyMap.get(step.status);
    const complete = currentIndex >= 0 ? index <= currentIndex : Boolean(history);
    const active = step.status === currentStatus;
    return {
      ...step,
      complete,
      active,
      at:
        history?.at ||
        (step.status === "created" ? order.createdAt : null) ||
        (step.status === "delivered" ? order.shipment?.deliveredAt : null) ||
        (step.status === "shipped" ? order.shipment?.shippedAt : null) ||
        (step.status === "pending-shipping" ? order.shipment?.packedAt : null),
      note: history?.note || "",
    };
  });

  if (FINAL_STEP_META[currentStatus]) {
    const finalHistory = historyMap.get(currentStatus);
    standardSteps.push({
      status: currentStatus,
      label: FINAL_STEP_META[currentStatus].label,
      complete: true,
      active: true,
      at: finalHistory?.at || order.updatedAt,
      note: finalHistory?.note || "",
    });
  }

  return standardSteps;
};

const getFulfillmentShipmentView = (order) => {
  const fulfillments = Array.isArray(order.sellerFulfillments) ? order.sellerFulfillments : [];
  const primary = fulfillments[0] || null;
  if (order.shipment?.courierName || order.shipment?.trackingNumber || order.shipment?.trackingUrl) {
    return order.shipment;
  }
  return primary || order.shipment || {};
};

const isDatePast = (value) => {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date < now;
};

const getDeliveryHealth = (order, shipmentView) => {
  const estimatedDelivery = shipmentView?.estimatedDelivery;
  const trackingReady = Boolean(shipmentView?.trackingNumber || shipmentView?.trackingUrl);
  const shipped = order.status === "shipped";
  const delivered = order.status === "delivered";
  const delayed = !delivered && Boolean(estimatedDelivery) && isDatePast(estimatedDelivery);

  if (delayed) {
    return {
      tone: "warning",
      label: "Delivery taking longer than expected",
      detail: "We will keep showing the latest shipment updates here until it arrives.",
    };
  }

  if (delivered) {
    return {
      tone: "success",
      label: "Delivered successfully",
      detail: shipmentView?.deliveredAt ? `Delivered on ${formatDateOnly(shipmentView.deliveredAt)}.` : "This order has been delivered.",
    };
  }

  if (shipped && trackingReady) {
    return {
      tone: "info",
      label: "Your package is on the way",
      detail: estimatedDelivery ? `Expected by ${formatDateOnly(estimatedDelivery)}.` : "Use live tracking for the latest courier movement.",
    };
  }

  if (order.status === "pending-shipping" || order.status === "accepted") {
    return {
      tone: "neutral",
      label: "Seller is preparing this order",
      detail: "Packing and courier assignment updates will appear here as soon as they are shared.",
    };
  }

  return {
    tone: "neutral",
    label: "Order received",
    detail: "We will post the next update here once payment and fulfillment move forward.",
  };
};

const getHelpActions = (order, fulfillments) => {
  const sellerNotifications = Array.isArray(order.notifications?.sellerNotifications)
    ? order.notifications.sellerNotifications
    : [];

  const sellerPhone = sellerNotifications.find((row) => row?.sellerPhone)?.sellerPhone || "";
  const sellerEmail = sellerNotifications.find((row) => row?.sellerEmail)?.sellerEmail || "";
  const sellerName =
    sellerNotifications.find((row) => row?.sellerName)?.sellerName ||
    fulfillments.find((row) => row?.sellerName)?.sellerName ||
    "Seller";

  const cleanPhone = String(sellerPhone || "").replace(/\D/g, "");
  const invoiceRef = order.invoiceNumber || order.razorpay_order_id || order._id;
  const message = encodeURIComponent(
    `Hello ${sellerName}, I need help with order ${invoiceRef}. Please share the latest update.`
  );

  return {
    sellerName,
    whatsappUrl: cleanPhone ? `https://wa.me/${cleanPhone}?text=${message}` : "",
    emailUrl: sellerEmail ? `mailto:${sellerEmail}?subject=${encodeURIComponent(`Help with order ${invoiceRef}`)}` : "",
    fallbackNote: cleanPhone || sellerEmail ? "" : "Support contact will appear here once the seller details are available.",
  };
};

const HEALTH_STYLES = {
  success: { bg: "#ecfdf5", border: "#a7f3d0", fg: "#065f46" },
  info: { bg: "#eff6ff", border: "#bfdbfe", fg: "#1d4ed8" },
  warning: { bg: "#fff7ed", border: "#fdba74", fg: "#9a3412" },
  neutral: { bg: "#f8fafc", border: "#e2e8f0", fg: "#334155" },
};

export default function OrderHistory() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [openOrderId, setOpenOrderId] = useState("");

  useEffect(() => {
    try {
      const c = JSON.parse(localStorage.getItem("customer") || "null");
      const token = typeof c?.token === "string" ? c.token : c?.token?.token;
      if (!token) {
        sessionStorage.setItem("redirectAfterLogin", "/orders");
        navigate("/customer-login");
        return;
      }
    } catch {
      sessionStorage.setItem("redirectAfterLogin", "/orders");
      navigate("/customer-login");
      return;
    }
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/api/orders/my");
      const list = Array.isArray(res.data) ? res.data : [];
      setOrders(list);
      setOpenOrderId((prev) => prev || list[0]?._id || "");
    } catch (e) {
      console.error("Failed to fetch orders:", e);
      setErr(e?.response?.data?.error || "Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const summary = useMemo(
    () => ({
      total: orders.length,
      active: orders.filter((order) => ["paid", "accepted", "pending-shipping", "shipped"].includes(order.status)).length,
      delivered: orders.filter((order) => order.status === "delivered").length,
      delayed: orders.filter((order) => {
        const shipmentView = getFulfillmentShipmentView(order);
        return getDeliveryHealth(order, shipmentView).tone === "warning";
      }).length,
    }),
    [orders]
  );

  if (loading) return <p style={{ padding: 20 }}>Loading your orders…</p>;
  if (err) return <p style={{ padding: 20, color: "red" }}>{err}</p>;

  return (
    <div style={{ maxWidth: 1120, margin: "0 auto", padding: "32px 20px 56px" }}>
      <div style={hero}>
        <div>
          <div style={eyebrow}>Order Center</div>
          <h2 style={{ margin: "10px 0 8px", fontSize: "clamp(2rem, 3vw, 3rem)", lineHeight: 1.05 }}>
            Track every order without guessing
          </h2>
          <p style={{ margin: 0, color: "#475569", maxWidth: 720 }}>
            See payment, packing, shipping, courier details, delivery address, invoice, and every item in one place.
          </p>
        </div>
        <div style={summaryGrid}>
          <div style={summaryCard}>
            <div style={summaryLabel}>Total orders</div>
            <div style={summaryValue}>{summary.total}</div>
          </div>
          <div style={summaryCard}>
            <div style={summaryLabel}>Active</div>
            <div style={summaryValue}>{summary.active}</div>
          </div>
          <div style={summaryCard}>
            <div style={summaryLabel}>Delivered</div>
            <div style={summaryValue}>{summary.delivered}</div>
          </div>
          <div style={summaryCard}>
            <div style={summaryLabel}>Delayed</div>
            <div style={summaryValue}>{summary.delayed}</div>
          </div>
        </div>
      </div>

      {orders.length === 0 ? (
        <div style={emptyState}>
          <h3 style={{ marginTop: 0 }}>You have no orders yet</h3>
          <p style={{ color: "#475569" }}>
            Once you place an order, its status, tracking, and invoice will appear here.
          </p>
          <Link to="/" style={shopLink}>Browse products</Link>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 18 }}>
          {orders.map((order) => {
            const rows = getOrderRows(order);
            const badge = STATUS_BADGES[order.status] || STATUS_BADGES.created;
            const isOpen = openOrderId === order._id;
            const timeline = buildTimeline(order);
            const freeGiftItems = order.promotions?.freeGiftItems || [];
            const fulfillments = Array.isArray(order.sellerFulfillments) ? order.sellerFulfillments : [];
            const shipmentView = getFulfillmentShipmentView(order);
            const deliveryHealth = getDeliveryHealth(order, shipmentView);
            const healthStyle = HEALTH_STYLES[deliveryHealth.tone] || HEALTH_STYLES.neutral;
            const helpActions = getHelpActions(order, fulfillments);

            return (
              <div key={order._id} style={orderCard}>
                <button
                  type="button"
                  onClick={() => setOpenOrderId((prev) => (prev === order._id ? "" : order._id))}
                  style={cardHeaderButton}
                >
                  <div style={{ display: "grid", gap: 10, textAlign: "left", flex: 1 }}>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                      <span style={{ ...badgeStyle, background: badge.bg, color: badge.fg }}>{badge.label}</span>
                      <span style={{ color: "#0f172a", fontWeight: 800 }}>
                        {order.invoiceNumber || order.razorpay_order_id || order._id}
                      </span>
                      <span style={{ color: "#64748b", fontSize: 14 }}>{formatDateTime(order.createdAt)}</span>
                    </div>
                    <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", alignItems: "center" }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 18 }}>
                          {rows.length} item{rows.length > 1 ? "s" : ""}
                        </div>
                        <div style={{ color: "#64748b", fontSize: 14 }}>
                          {rows.slice(0, 2).map((row) => row.item?.name || "Product").join(", ")}
                          {rows.length > 2 ? ` +${rows.length - 2} more` : ""}
                        </div>
                      </div>
                      <div>
                        <div style={miniLabel}>Total</div>
                        <div style={miniValue}>{formatCurrency(order.amount)}</div>
                      </div>
                      <div>
                        <div style={miniLabel}>Delivery</div>
                        <div style={miniValue}>{order.deliveryMode === "pickup" ? "Store pickup" : "Delivery"}</div>
                      </div>
                      <div>
                        <div style={miniLabel}>Tracking</div>
                        <div style={miniValue}>{shipmentView?.trackingNumber || "Will appear after dispatch"}</div>
                      </div>
                      <div>
                        <div style={miniLabel}>Expected delivery</div>
                        <div style={miniValue}>
                          {shipmentView?.estimatedDelivery ? formatDateOnly(shipmentView.estimatedDelivery) : "Will appear after dispatch"}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ color: "#0f172a", fontWeight: 800 }}>{isOpen ? "Hide details" : "View details"}</div>
                </button>

                {isOpen ? (
                  <div style={orderDetailGrid}>
                    <section
                      style={{
                        ...panel,
                        gridColumn: "1 / -1",
                        background: healthStyle.bg,
                        borderColor: healthStyle.border,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
                        <div style={{ display: "grid", gap: 6 }}>
                          <h3 style={{ ...panelTitle, marginBottom: 0, color: healthStyle.fg }}>{deliveryHealth.label}</h3>
                          <div style={{ color: "#334155" }}>{deliveryHealth.detail}</div>
                        </div>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          {shipmentView?.trackingUrl ? (
                            <a href={shipmentView.trackingUrl} target="_blank" rel="noreferrer" style={shopLink}>
                              Track package
                            </a>
                          ) : null}
                          <button type="button" onClick={() => downloadInvoice(order._id)} style={invoiceButton}>
                            Download invoice
                          </button>
                        </div>
                      </div>
                      <div style={snapshotGrid}>
                        <div style={snapshotCard}>
                          <div style={detailLabel}>Courier</div>
                          <div style={detailValue}>{shipmentView?.courierName || "Pending assignment"}</div>
                        </div>
                        <div style={snapshotCard}>
                          <div style={detailLabel}>Tracking number</div>
                          <div style={detailValue}>{shipmentView?.trackingNumber || "Will appear after dispatch"}</div>
                        </div>
                        <div style={snapshotCard}>
                          <div style={detailLabel}>Expected delivery</div>
                          <div style={detailValue}>
                            {shipmentView?.estimatedDelivery ? formatDateOnly(shipmentView.estimatedDelivery) : "Not shared yet"}
                          </div>
                        </div>
                      </div>
                    </section>

                    <section style={panel}>
                      <h3 style={panelTitle}>Order timeline</h3>
                      <div style={{ display: "grid", gap: 12 }}>
                        {timeline.map((step) => (
                          <div key={`${order._id}-${step.status}`} style={{ display: "grid", gridTemplateColumns: "18px 1fr", gap: 12, alignItems: "start" }}>
                            <div style={{ display: "grid", justifyItems: "center", paddingTop: 2 }}>
                              <span
                                style={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: "50%",
                                  background: step.complete ? "#0f766e" : "#cbd5e1",
                                  boxShadow: step.active ? "0 0 0 4px rgba(15,118,110,0.14)" : "none",
                                }}
                              />
                            </div>
                            <div>
                              <div style={{ fontWeight: 800, color: "#0f172a" }}>{step.label}</div>
                              <div style={{ color: "#64748b", fontSize: 14 }}>
                                {step.at ? formatDateTime(step.at) : "Waiting for this step"}
                              </div>
                              {step.note ? <div style={{ color: "#334155", fontSize: 14, marginTop: 4 }}>{step.note}</div> : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>

                    <section style={panel}>
                      <h3 style={panelTitle}>Shipping and tracking</h3>
                      <div style={{ display: "grid", gap: 10 }}>
                        <div>
                          <div style={detailLabel}>Delivery type</div>
                          <div style={detailValue}>{order.deliveryMode === "pickup" ? "Store pickup" : "Home delivery"}</div>
                        </div>
                        {order.deliveryMode === "delivery" ? (
                          <>
                            <div>
                              <div style={detailLabel}>Delivery address</div>
                              <div style={detailValue}>
                                {[order.shipping?.name, order.shipping?.phone].filter(Boolean).join(" • ")}
                                <br />
                                {[order.shipping?.line1, order.shipping?.line2, order.shipping?.city, order.shipping?.state, order.shipping?.pincode]
                                  .filter(Boolean)
                                  .join(", ")}
                              </div>
                            </div>
                            <div>
                              <div style={detailLabel}>Courier</div>
                              <div style={detailValue}>{shipmentView?.courierName || "Will be assigned after packing"}</div>
                            </div>
                            <div>
                              <div style={detailLabel}>Tracking number</div>
                              <div style={detailValue}>{shipmentView?.trackingNumber || "Not yet shared"}</div>
                            </div>
                            {shipmentView?.estimatedDelivery ? (
                              <div>
                                <div style={detailLabel}>Estimated delivery</div>
                                <div style={detailValue}>
                                  {formatDateOnly(shipmentView.estimatedDelivery)}
                                  {deliveryHealth.tone === "warning" ? (
                                    <span style={{ ...delayPill, marginLeft: 8 }}>Delayed</span>
                                  ) : null}
                                </div>
                              </div>
                            ) : null}
                            {shipmentView?.supportNote ? (
                              <div style={{ padding: 12, background: "#f8fafc", borderRadius: 14, color: "#334155" }}>
                                <div style={detailLabel}>Seller note</div>
                                <div style={{ marginTop: 4 }}>{shipmentView.supportNote}</div>
                              </div>
                            ) : null}
                            {fulfillments.length ? (
                              <div style={{ display: "grid", gap: 10, marginTop: 6 }}>
                                <div style={detailLabel}>Seller updates</div>
                                {fulfillments.map((fulfillment, index) => {
                                  const sellerBadge = STATUS_BADGES[fulfillment.status] || STATUS_BADGES.paid;
                                  return (
                                    <div key={`${order._id}-seller-fulfillment-${index}`} style={{ padding: 12, borderRadius: 14, border: "1px solid #e2e8f0", background: "#fff" }}>
                                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                                        <strong>{fulfillment.sellerName || "Seller"}</strong>
                                        <span style={{ ...badgeStyle, background: sellerBadge.bg, color: sellerBadge.fg }}>
                                          {sellerBadge.label}
                                        </span>
                                      </div>
                                      <div style={{ marginTop: 8, color: "#475569", fontSize: 14 }}>
                                        {fulfillment.courierName || "Courier pending"}
                                        {fulfillment.trackingNumber ? ` • ${fulfillment.trackingNumber}` : ""}
                                      </div>
                                      {fulfillment.supportNote ? (
                                        <div style={{ marginTop: 6, color: "#334155", fontSize: 14 }}>{fulfillment.supportNote}</div>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <div style={{ color: "#475569" }}>
                            This order is marked for store pickup. We will share readiness updates here.
                          </div>
                        )}
                      </div>
                    </section>

                    <section style={panel}>
                      <h3 style={panelTitle}>Need help with this order?</h3>
                      <div style={{ display: "grid", gap: 12 }}>
                        <div style={{ color: "#475569" }}>
                          If delivery feels delayed or you need a quick clarification, use the support option below. Your order reference is{" "}
                          <strong>{order.invoiceNumber || order.razorpay_order_id || order._id}</strong>.
                        </div>
                        <div style={snapshotGrid}>
                          <div style={snapshotCard}>
                            <div style={detailLabel}>Seller support</div>
                            <div style={detailValue}>{helpActions.sellerName}</div>
                          </div>
                          <div style={snapshotCard}>
                            <div style={detailLabel}>Delivery mode</div>
                            <div style={detailValue}>{order.deliveryMode === "pickup" ? "Store pickup" : "Home delivery"}</div>
                          </div>
                          <div style={snapshotCard}>
                            <div style={detailLabel}>Last order update</div>
                            <div style={detailValue}>{formatDateTime(order.updatedAt) || "Waiting for update"}</div>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          {helpActions.whatsappUrl ? (
                            <a href={helpActions.whatsappUrl} target="_blank" rel="noreferrer" style={shopLink}>
                              Chat on WhatsApp
                            </a>
                          ) : null}
                          {helpActions.emailUrl ? (
                            <a href={helpActions.emailUrl} style={secondaryLink}>
                              Email seller
                            </a>
                          ) : null}
                        </div>
                        {helpActions.fallbackNote ? <div style={{ color: "#64748b", fontSize: 14 }}>{helpActions.fallbackNote}</div> : null}
                      </div>
                    </section>

                    <section style={{ ...panel, gridColumn: "1 / -1" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                        <h3 style={panelTitle}>Items in this order</h3>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          {shipmentView?.trackingUrl ? (
                            <a href={shipmentView.trackingUrl} target="_blank" rel="noreferrer" style={secondaryLink}>
                              Open live tracking
                            </a>
                          ) : null}
                          <button type="button" onClick={() => downloadInvoice(order._id)} style={invoiceButton}>
                            Download invoice
                          </button>
                        </div>
                      </div>

                      <div style={{ display: "grid", gap: 14 }}>
                        {rows.map((row, index) => {
                          const product = row.item || {};
                          return (
                            <div key={`${order._id}-${index}`} style={itemRow}>
                              {product.imageUrl || product.image ? (
                                <img
                                  src={product.imageUrl || product.image}
                                  alt={product.name || "Product"}
                                  style={{ width: 84, height: 84, objectFit: "cover", borderRadius: 16 }}
                                />
                              ) : null}
                              <div style={{ display: "grid", gap: 6, flex: 1 }}>
                                {product._id ? (
                                  <Link to={`/product/${product._id}`} style={{ color: "#0f172a", fontWeight: 800, textDecoration: "none" }}>
                                    {product.name || "Product"}
                                  </Link>
                                ) : (
                                  <div style={{ color: "#0f172a", fontWeight: 800 }}>{product.name || "Product"}</div>
                                )}
                                <div style={{ color: "#64748b", fontSize: 14 }}>
                                  Qty {row.qty || 1} • {formatCurrency(row.price || product.price || 0)} each
                                </div>
                                <div style={{ color: "#64748b", fontSize: 14 }}>
                                  Seller: {product.sellerName || "Marketplace"}
                                </div>
                              </div>
                              <div style={{ fontWeight: 800 }}>{formatCurrency((row.price || product.price || 0) * Number(row.qty || 1))}</div>
                            </div>
                          );
                        })}

                        {freeGiftItems.length ? (
                          <div style={{ padding: 14, borderRadius: 16, background: "#faf5ff", color: "#6b21a8" }}>
                            <div style={{ fontWeight: 800, marginBottom: 8 }}>Included free gifts</div>
                            {freeGiftItems.map((gift, index) => (
                              <div key={`${order._id}-gift-${index}`} style={{ marginTop: index ? 6 : 0 }}>
                                {gift.name || "Free Gift"} x{gift.qty || 1}
                              </div>
                            ))}
                          </div>
                        ) : null}

                        <div style={totalsBox}>
                          <div style={totalsRow}>
                            <span>Order total</span>
                            <strong>{formatCurrency(order.amount)}</strong>
                          </div>
                          {order.promotions?.discountAmount ? (
                            <div style={{ ...totalsRow, color: "#0f766e" }}>
                              <span>Discount applied</span>
                              <strong>-{formatCurrency(order.promotions.discountAmount)}</strong>
                            </div>
                          ) : null}
                          {order.promotions?.freeShippingApplied ? (
                            <div style={{ ...totalsRow, color: "#1d4ed8" }}>
                              <span>Shipping offer</span>
                              <strong>Free shipping applied</strong>
                            </div>
                          ) : null}
                          {order.promotions?.freeGiftLabel ? (
                            <div style={{ ...totalsRow, color: "#7c3aed" }}>
                              <span>Offer benefit</span>
                              <strong>{order.promotions.freeGiftLabel}</strong>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </section>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const hero = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 18,
  alignItems: "start",
  marginBottom: 24,
};
const eyebrow = {
  display: "inline-flex",
  padding: "8px 12px",
  borderRadius: 999,
  background: "#dff7f2",
  color: "#0f766e",
  fontWeight: 800,
  textTransform: "uppercase",
  fontSize: 12,
  letterSpacing: 0.5,
};
const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: 12,
};
const summaryCard = {
  padding: 18,
  borderRadius: 22,
  background: "#fff",
  border: "1px solid #dbeafe",
  boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
};
const summaryLabel = {
  fontSize: 12,
  color: "#64748b",
  textTransform: "uppercase",
  fontWeight: 800,
};
const summaryValue = {
  marginTop: 8,
  fontSize: 28,
  fontWeight: 900,
};
const emptyState = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 26,
  padding: 24,
};
const orderCard = {
  background: "#fff",
  border: "1px solid #dbeafe",
  borderRadius: 26,
  boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
  overflow: "hidden",
};
const cardHeaderButton = {
  width: "100%",
  background: "transparent",
  border: "none",
  padding: 22,
  display: "flex",
  gap: 16,
  alignItems: "center",
  cursor: "pointer",
};
const badgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "6px 10px",
  borderRadius: 999,
  fontWeight: 800,
  fontSize: 12,
};
const miniLabel = {
  color: "#64748b",
  fontSize: 12,
  textTransform: "uppercase",
  fontWeight: 800,
};
const miniValue = {
  color: "#0f172a",
  fontWeight: 800,
  marginTop: 4,
  fontSize: 14,
};
const orderDetailGrid = {
  borderTop: "1px solid #e2e8f0",
  padding: 22,
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 18,
};
const panel = {
  border: "1px solid #e2e8f0",
  borderRadius: 20,
  padding: 18,
  background: "#fcfeff",
};
const panelTitle = {
  margin: "0 0 14px",
  fontSize: 18,
};
const detailLabel = {
  color: "#64748b",
  fontSize: 12,
  textTransform: "uppercase",
  fontWeight: 800,
  marginBottom: 4,
};
const detailValue = {
  color: "#0f172a",
  lineHeight: 1.5,
};
const shopLink = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px 14px",
  borderRadius: 12,
  background: "#0f766e",
  color: "#fff",
  textDecoration: "none",
  fontWeight: 800,
  width: "fit-content",
};
const invoiceButton = {
  padding: "10px 14px",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  cursor: "pointer",
  fontWeight: 800,
};
const secondaryLink = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px 14px",
  borderRadius: 12,
  background: "#fff",
  color: "#0f172a",
  textDecoration: "none",
  fontWeight: 800,
  border: "1px solid #cbd5e1",
  width: "fit-content",
};
const itemRow = {
  display: "flex",
  gap: 14,
  alignItems: "center",
  padding: 14,
  borderRadius: 18,
  background: "#fff",
  border: "1px solid #e2e8f0",
};
const totalsBox = {
  marginTop: 6,
  padding: 14,
  borderRadius: 18,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
};
const totalsRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  padding: "6px 0",
  color: "#334155",
};
const snapshotGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
  marginTop: 16,
};
const snapshotCard = {
  padding: 14,
  borderRadius: 16,
  background: "rgba(255,255,255,0.84)",
  border: "1px solid rgba(203,213,225,0.9)",
};
const delayPill = {
  display: "inline-flex",
  alignItems: "center",
  padding: "3px 8px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 800,
  background: "#fff7ed",
  color: "#9a3412",
  border: "1px solid #fdba74",
};
